import { useState, type FormEvent } from "react";
import { BRAND, login, verifyTotp } from "../api.js";

interface LoginPageProps {
  onLogin: () => void;
  onSso: () => void;
  ssoEnabled: boolean;
  ssoError: string | null;
}

const ssoMessages: Record<string, string> = {
  cancelled: "AAMOS-inloggningen avbröts. Du kan försöka igen eller använda reservinloggningen.",
  invalid_state: "Inloggningsförsöket kunde inte verifieras. Starta ett nytt försök.",
  expired_state: "Inloggningsförsöket hann gå ut. Starta ett nytt försök.",
  invalid_code: "AAMOS Login returnerade ett ogiltigt svar. Starta ett nytt försök.",
  not_authorized: `Kontot måste både tillhöra owners/platform-admins i AAMOS Login och vara admin i ${BRAND.name}.`,
  authentication_failed: "AAMOS-inloggningen kunde inte verifieras. Försök igen.",
  not_configured: `AAMOS Login är ännu inte aktiverat för ${BRAND.name} Admin.`,
};

export function LoginPage({ onLogin, onSso, ssoEnabled, ssoError }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 2FA-steget: sätts när kontot har TOTP aktiverat.
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null);
  const [code, setCode] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await login(email, password);
      if (result.totpRequired && result.preAuthToken) {
        setPreAuthToken(result.preAuthToken);
      } else {
        onLogin();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Inloggningen misslyckades.");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!preAuthToken) return;
    setBusy(true);
    setError(null);
    try {
      await verifyTotp(preAuthToken, code);
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fel engångskod.");
    } finally {
      setBusy(false);
    }
  };

  if (preAuthToken) {
    return (
      <div className="login-wrap">
        <form className="login-box" onSubmit={submitCode}>
          <h1>Tvåfaktorsautentisering</h1>
          <p className="muted">Ange den sexsiffriga koden från din autentiseringsapp.</p>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
            required
          />
          {error && <div className="error-text">{error}</div>}
          <button className="primary" disabled={busy || code.length !== 6}>
            {busy ? "Verifierar …" : "Verifiera"}
          </button>
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setPreAuthToken(null);
              setCode("");
              setError(null);
            }}
          >
            Avbryt
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-box">
        <h1>{BRAND.name} Admin</h1>
        <p className="muted login-intro">
          Använd samma säkra AAMOS Login-session som i EOC. Om du redan är inloggad går det normalt
          direkt.
        </p>
        {ssoError && (
          <div className="error-text" role="alert">
            {ssoMessages[ssoError] ?? "AAMOS-inloggningen misslyckades. Försök igen."}
          </div>
        )}
        <button type="button" className="primary login-sso" onClick={onSso} disabled={!ssoEnabled}>
          {ssoEnabled ? "Fortsätt med AAMOS Login" : "AAMOS Login är inte aktiverat"}
        </button>
        <details className="break-glass" open={!ssoEnabled}>
          <summary>Reservinloggning för driftavbrott</summary>
          <form onSubmit={submit}>
            <p className="muted">
              Använd endast om den centrala inloggningen inte fungerar. Lokal adminroll och TOTP
              krävs.
            </p>
            <input
              type="email"
              placeholder="E-post"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Lösenord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {error && <div className="error-text">{error}</div>}
            <button className="ghost" disabled={busy}>
              {busy ? "Loggar in …" : "Logga in lokalt"}
            </button>
          </form>
        </details>
      </div>
    </div>
  );
}
